import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const emptyParcel = (propertyId) => ({
  property_id: propertyId,
  section: "", number: "", lieu_dit: "", commune: "", surface: "", notes: "",
});

/** Cadastral parcels (French parcelles) attached to a property. */
export default function PropertyParcels({ propertyId, propertyName }) {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    if (!propertyId) return;
    const { data } = await api.get(`/parcels?property_id=${propertyId}`);
    setItems(data);
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    const body = {
      ...form,
      property_id: propertyId,
      property_name: propertyName || "",
      surface: form.surface === "" || form.surface == null ? null : parseFloat(form.surface),
    };
    try {
      if (form.id) await api.put(`/parcels/${form.id}`, body);
      else await api.post("/parcels", body);
      toast.success(t("saved"));
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/parcels/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const totalSurface = items.reduce((s, p) => s + (p.surface || 0), 0);

  return (
    <div data-testid="property-parcels" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-500">
          {items.length === 0 ? t("noParcelsForProperty") : t("parcelsForProperty")}
        </p>
        <Button data-testid="add-property-parcel" onClick={() => setDialog(emptyParcel(propertyId))} className="bg-stone-900 hover:bg-stone-800 text-white">
          <Plus size={15} className="mr-1.5" /> {t("addParcel")}
        </Button>
      </div>

      {items.length > 0 && (
        <ul className="divide-y divide-stone-100 border border-stone-200 rounded-lg overflow-hidden">
          {items.map((p) => (
            <li key={p.id} data-testid={`property-parcel-row-${p.id}`} className="group flex items-center gap-3 px-3 py-2 hover:bg-stone-50">
              <button
                onClick={() => setDialog({ ...p, surface: p.surface ?? "" })}
                data-testid={`edit-property-parcel-${p.id}`}
                className="flex-1 min-w-0 text-left flex items-center gap-2"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-stone-900 truncate">
                    <span className="font-medium">{p.commune || "—"}</span>
                    <span className="text-stone-500"> · {t("section")} {p.section || "—"} · N° {p.number || "—"}</span>
                  </p>
                  {p.lieu_dit && <p className="text-[11px] text-stone-400">{p.lieu_dit}</p>}
                </div>
                <Pencil size={13} strokeWidth={1.5} className="text-stone-300 group-hover:text-stone-600 flex-shrink-0" />
              </button>
              <span className="text-sm font-medium text-stone-900 tabular-nums w-28 text-right">
                {p.surface != null ? `${p.surface.toLocaleString()} m²` : "—"}
              </span>
              <button
                data-testid={`delete-property-parcel-${p.id}`}
                onClick={() => remove(p.id)}
                className="p-1 text-stone-300 hover:text-red-600 rounded transition-colors"
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {items.length > 0 && totalSurface > 0 && (
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-sm" data-testid="property-parcels-total">
          <p className="text-xs uppercase tracking-wider text-stone-500">{t("totalCadastralSurface")}</p>
          <p className="text-2xl font-heading font-medium text-stone-900 mt-1 tabular-nums">
            {totalSurface.toLocaleString()} m²
          </p>
        </div>
      )}

      {dialog && <ParcelDialog data={dialog} onClose={() => setDialog(null)} onSave={save} t={t} />}
    </div>
  );
}

function ParcelDialog({ data, onClose, onSave, t }) {
  const [form, setForm] = useState(data);
  useEffect(() => { setForm(data); }, [data]);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="property-parcel-dialog">
        <DialogHeader><DialogTitle>{form.id ? t("editParcel") : t("addParcel")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t("commune")}</Label>
            <Input data-testid="parcel-commune" value={form.commune || ""} onChange={(e) => setField("commune", e.target.value)} placeholder="e.g. Duras" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("section")}</Label>
              <Input data-testid="parcel-section" value={form.section || ""} onChange={(e) => setField("section", e.target.value)} placeholder="AB" />
            </div>
            <div>
              <Label>{t("parcelNumber")}</Label>
              <Input data-testid="parcel-number" value={form.number || ""} onChange={(e) => setField("number", e.target.value)} placeholder="123" />
            </div>
          </div>
          <div>
            <Label>{t("lieuDit")}</Label>
            <Input data-testid="parcel-lieu-dit" value={form.lieu_dit || ""} onChange={(e) => setField("lieu_dit", e.target.value)} />
          </div>
          <div>
            <Label>{t("surface")} (m²)</Label>
            <Input type="number" step="0.01" data-testid="parcel-surface" value={form.surface ?? ""} onChange={(e) => setField("surface", e.target.value)} />
          </div>
          <div>
            <Label>{t("notes")}</Label>
            <textarea rows={2} data-testid="parcel-notes" className="w-full text-sm border border-stone-200 rounded-lg p-2" value={form.notes || ""} onChange={(e) => setField("notes", e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose} data-testid="parcel-cancel">{t("cancel")}</Button>
          <Button data-testid="parcel-save" onClick={() => onSave(form)} className="bg-stone-900 hover:bg-stone-800 text-white">{t("save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
